import { SocialDocument } from '../../model/domain/social-document'
import { useTranslation } from 'react-i18next'
import { translate } from '../../../I18n/i18n'
import './DocumentsView.scss'
import {
    Alert,
    AlertType,
    LoadingView,
    Modal,
    Spinner,
    withAppStyle,
} from '@cuatroochenta/co-admin-react-library'
import SaveDocumentModal from './components/SaveDocumentModal/SaveDocumentModal'
import { EmptyContentText } from '../common/components/EmptyContentText/EmptyContentText'
import DocumentCard from './components/DocumentCard/DocumentCard'
import { useMemo } from 'react'
import { ShareCenterOption } from '../../model/domain/share-option'
import { User } from '../../model/domain/user'

interface DocumentsViewProps {
    user?: User
    loading: boolean
    error: string | null
    shareOptions?: ShareCenterOption[]
    loadingShareOptions: boolean
    socialDocuments: SocialDocument[]
    search: string
    loadingMore: boolean
    showSaveModal: boolean
    showDeleteModal: false
    loadingDetail: boolean
    selectedSocialDocument: SocialDocument | null
    saveSuccess: boolean
    deleteSuccess: boolean
    onSearchChange: (newSearch: string) => void
    onAddNewDocument: () => void
    onDocumentClick: (socialDocument: SocialDocument) => void
    onViewFile: () => void
    onDownloadFile: () => void
    onCancelSaveModal: () => void
    onConfirmSaveModal: (socialDocument: SocialDocument) => void
    onOpenDeleteModal: (socialDocumentId: SocialDocument['id']) => void
    onCancelDeleteModal: () => void
    onConfirmDeleteModal: () => void
}

function DocumentsView({
    user,
    loading,
    error,
    shareOptions,
    loadingShareOptions,
    socialDocuments,
    search,
    loadingMore,
    showSaveModal,
    showDeleteModal,
    loadingDetail,
    selectedSocialDocument,
    saveSuccess,
    deleteSuccess,
    onSearchChange,
    onAddNewDocument,
    onDocumentClick,
    onViewFile,
    onDownloadFile,
    onCancelSaveModal,
    onConfirmSaveModal,
    onOpenDeleteModal,
    onCancelDeleteModal,
    onConfirmDeleteModal,
}: DocumentsViewProps) {
    const { t } = useTranslation()

    const showAlert = useMemo(() => {
        return !!error || saveSuccess || deleteSuccess
    }, [error, saveSuccess, deleteSuccess])

    if (loading) {
        return <LoadingView loading={true} />
    }

    return (
        <>
            <Alert
                show={showAlert}
                type={error ? AlertType.DANGER : AlertType.SUCCESS}
                message={
                    error
                        ? error
                        : saveSuccess
                        ? translate(t, 'socialDocuments.TR_SAVE_SUCCESS')
                        : translate(t, 'socialDocuments.TR_DELETE_SUCCESS')
                }
            />
            <div className="toolbar">
                <div className="searchbar">
                    <i className="fa fa-search" />
                    <input
                        type="text"
                        value={search}
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder={translate(t, 'socialDocuments.TR_SEARCH')}
                    />
                </div>
                <button onClick={onAddNewDocument}>
                    <i className="fa fa-plus" />
                    {translate(t, 'socialDocuments.TR_ADD')}
                </button>
            </div>
            {socialDocuments.length === 0 ? (
                <EmptyContentText
                    message={translate(t, 'socialDocuments.TR_EMPTY_LABEL')}
                />
            ) : (
                <div className="documents-container">
                    {socialDocuments.map((socialDocument) => (
                        <DocumentCard
                            user={user}
                            socialDocument={socialDocument}
                            onDocumentClick={onDocumentClick}
                            onDeleteClick={onOpenDeleteModal}
                        />
                    ))}
                </div>
            )}
            <div className="loading-more-container">
                <Spinner loading={loadingMore} />
            </div>
            <SaveDocumentModal
                user={user}
                show={showSaveModal}
                loading={loadingDetail}
                socialDocument={selectedSocialDocument}
                shareOptions={shareOptions}
                loadingShareOptions={loadingShareOptions}
                onViewFile={onViewFile}
                onDownloadFile={onDownloadFile}
                onConfirm={onConfirmSaveModal}
                onCancel={onCancelSaveModal}
            />
            <Modal
                show={showDeleteModal}
                title={translate(t, 'socialDocuments.TR_DELETE_MODAL_TITLE')}
                children={
                    <p>
                        {translate(
                            t,
                            'socialDocuments.TR_DELETE_MODAL_MESSAGE'
                        )}
                    </p>
                }
                acceptHandler={onConfirmDeleteModal}
                onClose={onCancelDeleteModal}
            />
        </>
    )
}

export default withAppStyle(DocumentsView)
